import SysRecord from "app/class/record";
import { useState } from "react";
import { useRecordListContext } from "../hooks/useRecordListContext";
import useRecordListNavbarRelatedActions from "../hooks/useRecordListRelatedActions";
import { useRecordListRelatedActionCompiler } from "../hooks/useRecordListRelatedActionCompiler";
import { useSession } from "app/hooks/useSession";
import FormActionConfirmationDialog, { requiresFormActionConfirmation } from "modules/record/shared/components/FormActionConfirmationDialog";
import ResponsiveActionBar from "modules/record/shared/components/ResponsiveActionBar";
import useActionHotkeys from "modules/record/shared/hooks/useActionHotkeys";

interface RecordListRelatedActionsComponentProps {
    tableName: string;
}

const RecordListNavbarRelatedActionsComponent = ({
    tableName
}: RecordListRelatedActionsComponentProps) => {
    const { selectedIds } = useRecordListContext();
    const { data: userSession } = useSession();
    const { actions } = useRecordListNavbarRelatedActions(
        tableName,
        {
            currentTableName: tableName,
            selectedCount: selectedIds.length,
            hasSelection: selectedIds.length > 0,
            user: { id: userSession?.id || "", roles: userSession?.roles || [] }
        }
    );
    const { runActionScript } = useRecordListRelatedActionCompiler();

    const hasSelection = selectedIds.length > 0;

    const [pendingAction, setPendingAction] = useState<SysRecord | null>(null);
    const [runningActionId, setRunningActionId] = useState<string | null>(null);

    const executeAction = async (action: SysRecord) => {
        setRunningActionId(action.id);

        try {
            await runActionScript(action);
        } finally {
            setRunningActionId(null);
        }
    };

    const handleActionClick = (action: SysRecord) => {
        if (runningActionId) return;

        if (requiresFormActionConfirmation(action)) {
            setPendingAction(action);
            return;
        }

        executeAction(action);
    };

    const handleConfirmAction = async () => {
        const action = pendingAction;
        setPendingAction(null);

        if (action) {
            await executeAction(action);
        }
    };

    useActionHotkeys({
        actions,
        runningActionId,
        disabled: !hasSelection,
        onActionTrigger: handleActionClick,
    });

    if (!actions.length) {
        return null;
    }

    return (
        <>
            <ResponsiveActionBar
                actions={actions}
                runningActionId={runningActionId}
                disabled={!hasSelection}
                mobileVisibleCount={0}
                desktopVisibleCount={0}
                menuLabel="Related actions"
                mobileMenuLabel="Related"
                showMobileMenuLabel
                variant="inline"
                onActionClick={handleActionClick}
            />
            <FormActionConfirmationDialog
                action={pendingAction}
                open={Boolean(pendingAction)}
                onCancel={() => setPendingAction(null)}
                onConfirm={handleConfirmAction}
            />
        </>
    );
};

export default RecordListNavbarRelatedActionsComponent;
